/**
 * Fluxible store used to manage lower level staff memberships data, designed
 * for use with staff-management.
 *
 * The main job of the store is to provide essential data, a consistent update API,
 * and observable changes in a React friendy way on change.
 */
import _ from 'lodash';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import { EVENT_NAMES, STORE_NAME } from './constants';
import type {
  EmailableInterface,
  ErrorInterface,
  NotificationData,
  StaffMembershipInterface,
  StaffMembershipRoleInterface,
  StaffMembershipType,
  StaffMembershipUserInterface,
} from './types';

const { DATA, MERGE, NOTIFICATIONS, PARTNER_ROLE_TEMPLATES, RESET, SET, UI, UPDATE } = EVENT_NAMES;

/**
 * If implemented, the current activity state of a particular store.
 *
 * If used, stores should be sized/scoped appropriately that this style of activity
 * block makes sense and doesn't confuse the idea of what is busy and when.
 *
 * Activity states are internally stored as a number >= 0, with 0 being not happening
 * and >= 0 being something is happening. Public APIs will most often work with a boolean
 * value.
 */
export type StateActivityInterface = {
  creating?: boolean | number;
  deleting?: boolean | number;
  initializing?: boolean | number;
  loading?: boolean | number;
  updating?: boolean | number;
};

export const isStateActivityInterface = (s: string) =>
  s in { creating: true, deleting: true, initializing: true, loading: true, updating: true };

export type StaffManagementStateData = {
  /** Error retrieving data? */
  error?: ErrorInterface;
  /** memberships indexed by id. */
  memberships?: {
    [id: string]: StaffMembershipInterface;
  };
  /** roles indexed by id. */
  roles?: {
    [id: string]: StaffMembershipRoleInterface;
  };
  /** users indexed by id. */
  users?: {
    [id: number]: StaffMembershipUserInterface;
  };
} & StateActivityInterface;

export type StaffManagementStatePartnerRoleTemplates = {
  /** Error retrieving data? */
  error?: ErrorInterface;
  /** What roles are available for users. */
  templates?: {
    // Indexed by id role id.
    [key: string]: StaffMembershipRoleInterface;
  };
} & StateActivityInterface;

/** State for more complex UI flows. */
export type StaffManagementStateUI = {
  /** Add flow. */
  addFlow?: {
    /** To which entity we will be inviting members. */
    fromId?: string;
    /** List of roles by id to assign users as members of. */
    roles?: Array<string>;
    /** Display modal. */
    show: boolean;
    /** Which type of entity we will be inviting to. */
    type?: StaffMembershipType;
    /** List of users identified by email that we wish to add memberships to. */
    users?: Array<EmailableInterface>;
  };
  /** Deletion flow. */
  delFlow?: {
    /** From which entity we will be deleting linked memberships. */
    fromId?: string;
    /** Optionally, a distinct list of memberships can be requested to be removed. */
    memberships?: Array<string>;
    /** From which type of entity we will be deleting linked memberships. */
    type?: StaffMembershipType;
    /** List of users from which to delete linked memberships from. */
    users?: Array<Partial<StaffMembershipUserInterface>>;
  };
  /** Membership update flow. */
  editFlow?: {
    /** From which entity we will be modifying memberships. */
    fromId?: string;
    /** List of roles by id to set users as members of. */
    roles?: Array<string>;
    /** From which type of entity we will be modifying linked memberships. */
    type?: StaffMembershipType;
    /** List of users whom will get their memberships updated. */
    users?: Array<Partial<StaffMembershipUserInterface>>;
  };
  /** Invite new users to coursera. */
  inviteFlow?: {
    /** To which entity we will be inviting members. */
    fromId?: string;
    /** List of roles by id that we are inviting the users to be a part of. */
    roles?: Array<string>;
    /** Which type of entity we will be inviting to. */
    type?: StaffMembershipType;
    /** List of users identified by email that we wish to invite to coursera. */
    users?: Array<string>;
  };
  /* UI for "work in progress" code. */
  paintedDoor?: {
    /** Display modal message. */
    show: boolean;
  };
};

export type StaffManagementState = {
  /** Id of the `type` of entity we are working with. */
  fromId?: string | number;
  data?: StaffManagementStateData;
  /** Partner id entity is associated with. Needed to retrieve partnerRoleTemplates as 
      they can only be retrieved by partnerId. */
  partnerId?: string | number;
  /** Queue of notifications. */
  notifications: Array<NotificationData>;
  partnerRoleTemplates?: StaffManagementStatePartnerRoleTemplates;
  /** Which type of entity are we working with. */
  type?: StaffMembershipType;
  ui?: StaffManagementStateUI;
};

export class StaffManagementStoreV2 extends BaseStore {
  static defaultState: StaffManagementState = {
    data: {
      memberships: {},
      roles: {},
      users: {},

      error: undefined,

      creating: 0,
      deleting: 0,
      // Assumed initializing to aid UI.
      initializing: 1,
      loading: 0,
      updating: 0,
    },
    partnerRoleTemplates: {
      templates: undefined,

      error: undefined,
      // Assumed initializing to aid UI.
      initializing: 1,
      loading: 0,
    },
    notifications: [],
    ui: {
      addFlow: {
        fromId: undefined,
        roles: [],
        show: false,
        type: undefined,
        users: [],
      },
      editFlow: {
        fromId: undefined,
        roles: [],
        type: undefined,
        users: [],
      },
      delFlow: {
        fromId: undefined,
        memberships: [],
        type: undefined,
        users: [],
      },
      inviteFlow: {
        fromId: undefined,
        roles: [],
        type: undefined,
        users: [],
      },
      paintedDoor: {
        show: false,
      },
    },
  };

  static storeName = STORE_NAME;

  static handlers = {
    // Specific sub-state handlers.
    [DATA]: 'data',
    [NOTIFICATIONS]: 'notifications',
    [PARTNER_ROLE_TEMPLATES]: 'partnerRoleTemplates',
    [UI]: 'ui',

    // Generic state handlers.
    [MERGE]: 'merge',
    [RESET]: 'reset',
    [SET]: 'set',
    [UPDATE]: 'update',
  };

  state: StaffManagementState = StaffManagementStoreV2.defaultState;

  /**
   * Provide the basis for a getter/setter for sub-states as objects.
   */
  _subStateObjectGetUpdate<K, V>(stateKey: 'data' | 'partnerRoleTemplates' | 'ui', keyOrValue?: string | K | V) {
    if (keyOrValue && typeof keyOrValue !== 'string') {
      // Update.
      const existing = _.get(this.state, stateKey);
      const updatedState = { ...this.state };
      updatedState[stateKey] = {
        ...existing,
        ...keyOrValue,
      };
      this.state = updatedState;
      return this.emitChange();
    } else if (typeof keyOrValue === 'string') {
      // Get one value by key.
      return _.get(this.state, [stateKey, keyOrValue]);
    }
    // Get all key:values.
    return this.state[stateKey];
  }

  /**
   * Internally activity is a counter, but we allow public access via boolean during
   * dispatched events.
   */
  _processActivity<S extends StateActivityInterface>(state: S, payload: S) {
    let didActivityUpdate = false;
    const updatedActivity = _.reduce(
      payload,
      (activity, value, key) => {
        if (isStateActivityInterface(key)) {
          didActivityUpdate = true;
          // If the incoming payload expresses activity as a boolean, increment or
          // decrement the acitivity counter.
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          activity[key] = Math.max(0, _.get(state, key, 0) + (value ? 1 : -1)); // eslint-disable-line no-param-reassign
        }
        return activity;
      },
      {}
    );
    if (didActivityUpdate) {
      // Update payload with corrected activity counters.
      return { ...payload, ...updatedActivity };
    }
    return null;
  }

  /**
   * Interact with the data sub-state.
   *
   * Main usage:
   *
   * getter of all activity: `.data()`
   * getter of one key: `.data('users')`
   * setter, as dispatch handler: `.data({users: ['bob', ...]})`
   */
  // @ts-expect-error ts-migrate(7023) FIXME: 'data' implicitly has return type 'any' because it... Remove this comment to see the full error message
  data<K extends StaffManagementStateData>(keyOrValue?: string | K) {
    let updatedPayload;
    if (keyOrValue && typeof keyOrValue !== 'string') {
      // Update payload if necessary.
      updatedPayload = this._processActivity<K>(this.data(), keyOrValue);
    }
    return this._subStateObjectGetUpdate<K, K>('data', updatedPayload || keyOrValue);
  }

  /**
   * Interact with the notifications sub-state.
   *
   * Main usage:
   *
   * getter of all notifications: `.notifications()`
   * set the queue: `.notifications([{message: 'blah blah', type: 'info'}, ...])`
   */
  notifications(notifications?: Array<NotificationData>) {
    // Some argument === setter or command.
    if (Array.isArray(notifications)) {
      // Setter
      this.state = {
        ...this.state,
        notifications,
      };
      return this.emitChange();
    }
    // No arguments === getter.
    return this.state.notifications;
  }

  /**
   * Interact with the partnerRoleTemplates sub-state.
   *
   * Main usage:
   *
   * getter of all activity: `.partnerRoleTemplates()`
   * getter of one key: `.partnerRoleTemplates('roles')`
   * setter, as dispatch handler: `.partnerRoleTemplates({roles: [...]})`
   */
  // @ts-expect-error ts-migrate(7023) FIXME: 'partnerRoleTemplates' implicitly has return type ... Remove this comment to see the full error message
  partnerRoleTemplates<K extends StaffManagementStatePartnerRoleTemplates>(keyOrValue?: string | K) {
    let updatedPayload;
    if (keyOrValue && typeof keyOrValue !== 'string') {
      // Update payload if necessary.
      updatedPayload = this._processActivity<K>(this.partnerRoleTemplates(), keyOrValue);
    }
    return this._subStateObjectGetUpdate<K, K>('partnerRoleTemplates', updatedPayload || keyOrValue);
  }

  /**
   * Interact with the ui sub-state.
   *
   * Main usage:
   *
   * getter of all activity: `.ui()`
   * getter of one key: `.ui('del')`
   * setter, as handler: `.data({users: ['bob', ...]})`
   */
  ui<K extends StaffManagementStateUI>(keyOrValue?: string | K) {
    return this._subStateObjectGetUpdate<K, K>('ui', keyOrValue);
  }

  /**
   * Get reference to current state.
   */
  get(): StaffManagementState {
    return this.state;
  }

  /**
   * Deep merge payload into `this.state`, using [lodash.merge](https://lodash.com/docs/4.17.15#merge).
   *
   * Generic state manipulation.
   */
  merge(payload: StaffManagementState) {
    this.state = _.merge({}, this.state, payload);
    this.emitChange();
  }

  /**
   * Reset `this.state` to the default state.
   *
   * Generic state manipulation.
   */
  reset() {
    this.state = StaffManagementStoreV2.defaultState;
    this.emitChange();
  }

  /**
   * Overwrite existing `this.state` with payload.
   *
   * Generic state manipulation.
   */
  set(payload: StaffManagementState) {
    _.set(this, 'state', payload);
    this.emitChange();
  }

  /**
   * Shallow merge payload into `this.state`.
   *
   * Generic state manipulation.
   */
  update(payload: StaffManagementState) {
    this.state = {
      ...this.state,
      ...payload,
    };
    this.emitChange();
  }

  /**
   * Implementation of Fluxible interface method.
   */
  dehydrate(): StaffManagementState {
    return this.state;
  }

  /**
   * Implementation of Fluxible interface method.
   */
  rehydrate(state: StaffManagementState) {
    this.state = state;
  }
}

export default StaffManagementStoreV2;
